import React, { useState } from 'react';
import { Zap, Shield, Globe, TrendingUp, Clock, Users, ArrowRight } from 'lucide-react';
import UT from "../assets/utATX.png";
import afscme from "../assets/AFSCME_logo.png";
import MBT from "../assets/MBT Hand Logo2.png";
import kp from "../assets/kaiser-permanente.png";
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'Deploy AI solutions in minutes, not months',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-level encryption and compliance',
  },
  {
    icon: Globe,
    title: 'Global Scale',
    description: 'Reach customers worldwide instantly',
  },
  {
    icon: TrendingUp,
    title: 'Smart Analytics',
    description: 'Real-time insights and performance metrics',
  },
  {
    icon: Clock,
    title: 'Always Online',
    description: 'Zero downtime with 99.9% uptime SLA',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Built for teams of all sizes',
  },
];

const caseStudies = [
  {
    company: 'Tech-SaaS',
    title: 'Reduced Support Costs by 65%',
    description: "SaaS company deployed intelligent chatbot system to handle tier-1 support inquiries, improving response times from 8 hours to under 5 minutes.",
    result: '65% Cost Reduction',
    metric: '3 Months',
  },
  {
    company: 'MedSpa',
    title: 'Increased Productivity 3x',
    description: "AI Front Desk Employee handles inbound calls, SMS, and web chat 24/7, automating appointments and freeing staff to focus on patient care.",
    result: '3x Productivity',
    metric: '8 Weeks',
  },
  {
    company: 'Real Estate',
    title: 'Lead Conversion Increased to 62%',
    description: "AI Sales Agent responds to leads in under 60 seconds, qualifies buyers automatically, and schedules showings around the clock.",
    result: '62% Conversion',
    metric: '90 Days',
  },
];

export default function Features() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = React.useRef(null);

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const cardWidth = carouselRef.current.offsetWidth;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(newIndex);
    }
  };

  const scrollToIndex = (index) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: cardWidth * index,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="services" className="relative py-20 px-4 sm:px-6">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent" />

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 px-4">
            Built for
            <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent"> Performance</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto px-4">
            Enterprise-grade features that scale with your ambitions
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 sm:p-8 hover:border-blue-500/50 transition-all duration-300 hover:transform hover:-translate-y-2"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <feature.icon className="w-6 h-6 text-blue-400" />
              </div>

              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                {feature.title}
              </h3>

              <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 sm:mt-24 text-center py-14">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-4">
            Trusted by Industry Leaders
          </h3>
          <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-12 max-w-2xl mx-auto px-4">
            Join major companies using NeuroStudio to revolutionize their operations
          </p>

          {/* Logo Scroll Container */}
          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-slate-950 to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-slate-950 to-transparent z-10" />

            <div className="flex animate-scroll">
              {/* First scroll set */}
              <div className="flex items-center space-x-8 sm:space-x-16 px-4 sm:px-8">
                <div className="w-[100px] sm:w-[150px] flex items-center justify-center flex-shrink-0">
                  <img src={UT} className="max-w-full h-auto" alt="UT Austin" />
                </div>
                <div className="w-[100px] sm:w-[150px] flex items-center justify-center flex-shrink-0">
                  <img src={afscme} className="max-w-full h-auto" alt="AFSCME" />
                </div>
                <div className="w-[150px] sm:w-[250px] flex items-center justify-center flex-shrink-0">
                  <img src={kp} className="max-w-full h-auto" alt="Kaiser Permanente" />
                </div>
                <div className="w-[80px] sm:w-[120px] flex items-center justify-center flex-shrink-0">
                  <img src={MBT} className="max-w-full h-auto" alt="MBT" />
                </div>
                <div className="w-[100px] sm:w-[150px] h-16 sm:h-20 bg-slate-800/50 rounded-lg border border-slate-700/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-500 font-semibold text-center text-xs sm:text-sm px-2">
                    Your Logo<br />Here
                  </span>
                </div>
              </div>

              {/* Duplicate for seamless scroll */}
              <div className="flex items-center space-x-8 sm:space-x-16 px-4 sm:px-8" aria-hidden="true">
                <div className="w-[100px] sm:w-[150px] flex items-center justify-center flex-shrink-0">
                  <img src={UT} className="max-w-full h-auto" alt="UT Austin" />
                </div>
                <div className="w-[100px] sm:w-[150px] flex items-center justify-center flex-shrink-0">
                  <img src={afscme} className="max-w-full h-auto" alt="AFSCME" />
                </div>
                <div className="w-[150px] sm:w-[250px] flex items-center justify-center flex-shrink-0">
                  <img src={kp} className="max-w-full h-auto" alt="Kaiser Permanente" />
                </div>
                <div className="w-[80px] sm:w-[120px] flex items-center justify-center flex-shrink-0">
                  <img src={MBT} className="max-w-full h-auto" alt="MBT" />
                </div>
                <div className="w-[100px] sm:w-[150px] h-16 sm:h-20 bg-slate-800/50 rounded-lg border border-slate-700/50 flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-500 font-semibold text-center text-xs sm:text-sm px-2">
                    Your Logo<br />Here
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-16">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4 text-center">
            Success Stories
          </h3>
          <p className="text-base md:text-lg text-slate-400 mb-8 md:mb-16 max-w-2xl mx-auto text-center px-4">
            See how industry leaders are transforming their business with NeuroStudio
          </p>

          <div className="relative max-w-7xl mx-auto">
            <div
              ref={carouselRef}
              className="overflow-x-auto snap-x snap-mandatory scrollbar-hide flex gap-4 md:gap-6"
              onScroll={handleScroll}
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {caseStudies.map((study, index) => (
                <div
                  key={index}
                  className="w-full flex-shrink-0 snap-center"
                >
                  <div className="group bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl md:rounded-3xl p-6 md:p-8 hover:border-blue-500/50 transition-all duration-300 h-full">
                    <div className="flex flex-col h-full">
                      <div className="mb-6">
                        <span className="inline-block px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded-full text-xs md:text-sm font-semibold text-blue-300 mb-3 md:mb-4">
                          {study.company}
                        </span>
                        <h4 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
                          {study.title}
                        </h4>
                        <p className="text-sm md:text-base text-slate-300 leading-relaxed mb-4 md:mb-6">
                          {study.description}
                        </p>
                      </div>

                      <div className="mt-auto">
                        <div className="grid grid-cols-2 gap-3 md:gap-4 pt-4 md:pt-6 border-t border-slate-700/50">
                          <div>
                            <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                              {study.result}
                            </div>
                            <div className="text-xs md:text-sm text-slate-400">Results</div>
                          </div>
                          <div>
                            <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent mb-1">
                              {study.metric}
                            </div>
                            <div className="text-xs md:text-sm text-slate-400">Timeframe</div>
                          </div>
                        </div>

                        <Link to="/case-studies" className="mt-4 md:mt-6 flex items-center text-blue-400 group-hover:text-blue-300 transition-colors">
                          <span className="text-sm md:text-base font-semibold">Read Case Study</span>
                          <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dot Navigation */}
            <div className="flex justify-center gap-2 md:gap-3 mt-6 md:mt-8">
              {caseStudies.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`h-2 md:h-3 rounded-full transition-all duration-300 ${currentIndex === index
                    ? 'bg-blue-500 w-6 md:w-8'
                    : 'bg-slate-600 hover:bg-slate-500 w-2 md:w-3'
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}